import React, { useState, useEffect } from "react";
import Layout from "../../layouts";
import { toast } from "react-toastify";
import DynamicTable from "../../components/DynamicTable";
import apiService from "../../services/apiService";
import { render } from "@testing-library/react";
import { Key } from "lucide-react";
import { withSearchIcon } from "../../utils/helper";

export default function EventPayouts() {
  const [page, setPage] = useState(1);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [approvePayouts, setApprovePayouts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"requested" | "completed">(
    "requested"
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  const [approving, setApproving] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    eventId: string | null;
    eventName?: string;
  }>({
    open: false,
    eventId: null,
  });

  const [customModal, setCustomModal] = useState<{
    open: boolean;
    eventId?: string;
    amount?: string;
    eventName?: string;
  }>({ open: false });
  const [savingCustom, setSavingCustom] = useState(false);

  const [newPayoutModal, setNewPayoutModal] = useState(false);
  const [searchEvent, setSearchEvent] = useState("");
  const [eventResults, setEventResults] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [creating, setCreating] = useState(false);

  // ✅ Fetch events for the search field
  const fetchEventList = async (searchKey: string) => {
    if (!searchKey.trim()) return;
    try {
      const response: any = await apiService.get("/events/get-events", {
        search: searchKey,
        limit: 10000,
        page: 1,
      });
      setEventResults(response?.data?.data || []);
    } catch (err) {
      console.error("Error fetching events:", err);
      toast.error("Failed to fetch events");
    }
  };

  // ✅ Handle payout request creation
  const handleCreatePayout = async () => {
    if (!selectedEvent?.eventId) {
      toast.error("Please select an event");
      return;
    }
    try {
      setCreating(true);
      const response: any = await apiService.post(
        "/event/payout/request-payout",
        {
          eventId: selectedEvent.eventId,
        }
      );

      if (response?.code === 200 || response?.data?.success) {
        toast.success("Payout request created successfully!");
        setNewPayoutModal(false);
        fetchPayouts(page, activeTab); // refresh list
      } else {
        toast.error("Failed to create payout request");
      }
    } catch (error: any) {
      console.error("Create payout error:", error);
      toast.error(
        error?.response?.data?.error ||
          "Something went wrong while creating payout"
      );
    } finally {
      setCreating(false);
    }
  };

  // ✅ Define Columns Dynamically Based on Active Tab

  const approvedRequestColumns = [
    { key: "eventId", label: withSearchIcon("Event ID") },
    { key: "eventName", label: withSearchIcon("Event Name") },
    {
      key: "hostUserId",
      label: "Host Id",
    },
    {
      key: "hostName",
      label: "Host Name",
    },
    {
      key: "hostEmail",
      label: "Host Email",
    },
    {
      key: "amount",
      label: "Amount ($)",
      render: (val: number) => (val != null ? `$${val}` : "—"),
      isDecimal: true,
    },

    {
      key: "transactionId",
      label: "Transaction ID",
    },

    {
      key: "payoutStatus",
      label: "Status",
    },
  ];

  const requestedColumns = [
    { key: "eventId", label: withSearchIcon("Event ID")},

    { key: "eventName", label: withSearchIcon("Event Name") },
    {
      key: "hostUserId",
      label: "Host Id",
    },
    {
      key: "hostName",
      label: withSearchIcon("Host Name"),
    },

    { key: "hostEmail", label: "Host Email" },
    {
      key: "amount",
      label: "Payout Amount ($)",
      render: (val: number) => (val != null ? `$${val}` : "—"),
      isDecimal: true,
    },
    {
      key: "status",
      label: "Payout Status",
      render: (val: number) => (val ? "Requested" : "—"),
    },

    // ⭐ BUTTONS AT END (AS REQUESTED)
    {
      key: "actions",
      label: "Actions",
      className:
        "px-4 py-3 whitespace-nowrap text-right sticky right-0 z-10 rounded bg-white",
      render: (_: any, row: any) => (
        <div className="flex justify-end gap-2">
          {/* Custom Payout */}
          <button
            onClick={() =>
              setCustomModal({
                open: true,
                eventId: row?.eventId,
                amount: "",
                eventName: row?.eventName,
              })
            }
            className="px-2 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
          >
            Custom Payout
          </button>

          {/* Approve */}
          <button
            onClick={() =>
              setConfirmModal({
                open: true,
                eventId: row.eventId,
                eventName: row.eventName,
              })
            }
            className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
          >
            Approve
          </button>
        </div>
      ),
    },
  ];
  // ✅ Fetch when tab or page changes
  useEffect(() => {
    fetchPayouts(page, activeTab, search);
  }, [page, activeTab, search]);
  const handleApprovePayout = async (eventId: string) => {
    try {
      setApproving(eventId);
      const response: any = await apiService.post(
        `/event/payout/approve-payout`,
        {
          eventId, // ✅ correct payload
        }
      );

      if (response?.code === 200 || response?.success) {
        toast.success(response?.message || "Payout approved successfully!");
        // 🔁 Refresh requested payouts list
        fetchPayouts(page, activeTab);
        setConfirmModal({ open: false, eventId: null });
      } else {
        toast.error("Failed to approve payout");
        setConfirmModal({ open: false, eventId: null });
      }
    } catch (error: any) {
      console.error("Approve payout error:", error?.response || error);
      toast.error(
        error?.response?.data?.error ||
          "Something went wrong while approving payout"
      );
    } finally {
      setApproving(null);
      // setConfirmModal({ open: false, eventId: null });
    }
  };

  // ✅ Tab Config
  const tabs = [
    { label: "Requested Payouts", key: "requested" },
    { label: "Approved Payouts", key: "completed" },
  ];

  // ✅ Fetch data based on tab + pagination
  const fetchPayouts = async (
    pageNum: number,
    tabKey: string,
    search?: any
  ) => {
    try {
      setLoading(true);

      let endpoint = "/event/payout/get-requested-payouts";

      if (tabKey == "completed") {
        endpoint = "/event/payout/get-approved-payouts";
      }

      const response: any = await apiService.get(endpoint, {
        page: pageNum,
        limit: 50,
        search: search,
      });
      setPayouts(response?.data?.data || response?.data?.events || []);
      setTotalRecords(
        response?.data?.totalCount || response?.data?.total_count || 0
      );
      setTotalPages(
        response?.data?.totalPages || response?.data?.total_pages || 1
      );
    } catch (err) {
      console.error("Error fetching payouts:", err);
      toast.error("Failed to fetch payouts");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveCustomPayout = async (eventId: string, amount: string) => {
    if (!amount || Number(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      setSavingCustom(true);

      const response: any = await apiService.post(
        "/event/payout/approve-custom-payout",
        { eventId: eventId, amount: Number(amount) }
      );

      if (response?.code === 200 || response?.data?.success) {
        toast.success("Custom payout approved successfully!");
        setCustomModal({ open: false });
        fetchPayouts(page, activeTab); // refresh list
      } else {
        toast.error("Failed to approve custom payout");
      }
    } catch (error: any) {
      console.error("Custom payout error:", error);
      toast.error(
        error?.response?.data?.error ||
          "Something went wrong while approving custom payout"
      );
    } finally {
      setSavingCustom(false);
    }
  };

  // ✅ Reset pagination when switching tabs
  const handleTabChange = (key: "requested" | "completed") => {
    setSearch("");
    setActiveTab(key);
    setPage(1);
  };

  return (
    <Layout
      pageTitle="Payouts"
      actionButton={{
        label: "Create Payout Request",
        onClick: () => {
          setSearchEvent("");
          setNewPayoutModal(true);
        }, // 👈 your custom handler
        className: "bg-green-600 hover:bg-green-700",
      }}
    >
      <div className="min-h-screen bg-gray-50 py-3 px-4 min-w-0">
        {/* ===== Tabs ===== */}
        <div className="flex bg-white border-b overflow-x-auto">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key as any)}
                className={`py-3 px-5 text-sm font-medium rounded-t-lg transition-all duration-200 whitespace-nowrap ${
                  isActive
                    ? "bg-blue-50 text-blue-700 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-blue-600 hover:bg-gray-50 border-b-2 border-transparent"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ===== Table ===== */}
        <div className="py-2 px-2">
          {/* {loading ? (
            <div className="text-center text-gray-500 py-6">
              Loading payouts...
            </div>
          ) : (
            
          )} */}

          <DynamicTable
            key={activeTab}
            title={
              activeTab == "requested"
                ? "Requested Payouts"
                : "Approved Payouts"
            }
            data={payouts}
            columns={
              activeTab == "completed"
                ? approvedRequestColumns
                : requestedColumns
            }
            pageSize={50}
            loading={loading}
            activeNUmber={page}
            onPageChange={(newPage) => setPage(newPage)}
            totalRecords={totalRecords}
            totalPages={totalPages}
            onSearch={(value?: any) => {
              setActiveTab(activeTab);
              setPage(1);
              setSearch(value);
            }}
            hasTabs={true}
          />
        </div>
        {/* ✅ Confirmation Modal */}
        {confirmModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">
                Confirm Payout Approval
              </h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to approve payout for event{" "}
                <span className="font-medium text-gray-900">
                  {confirmModal.eventName} (Event ID: {confirmModal.eventId})
                </span>
                ?
              </p>
              <div className="flex justify-end gap-3">
                {approving === confirmModal.eventId ? (
                  ""
                ) : (
                  <button
                    onClick={() =>
                      setConfirmModal({ open: false, eventId: null })
                    }
                    className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                )}
                <button
                  disabled={approving === confirmModal.eventId}
                  onClick={() => {
                    if (!approving) handleApprovePayout(confirmModal.eventId!);
                  }}
                  className={`px-4 py-2 text-sm rounded text-white transition ${
                    approving === confirmModal.eventId
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {approving === confirmModal.eventId
                    ? "Processing..."
                    : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}

        {customModal.open && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 w-[90%] max-w-md">
              <h2 className="text-lg font-semibold mb-3">
                Approve Payout with Custom Amount
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Enter a custom payout amount for event{" "}
                <strong>
                  {customModal.eventName} (Event ID: {customModal.eventId})
                </strong>
              </p>

              <input
                type="number"
                min="1"
                step="0.01"
                value={customModal.amount || ""}
                onChange={(e) =>
                  setCustomModal((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 focus:outline-none focus:ring focus:ring-blue-200"
                placeholder="Enter custom amount"
              />

              <div className="flex justify-end gap-3">
                {savingCustom ? (
                  ""
                ) : (
                  <button
                    onClick={() => setCustomModal({ open: false })}
                    className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                )}
                <button
                  disabled={savingCustom}
                  onClick={() =>
                    handleApproveCustomPayout(
                      customModal.eventId!,
                      customModal.amount!
                    )
                  }
                  className={`px-4 py-2 text-sm text-white rounded ${
                    savingCustom
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-orange-600 hover:bg-orange-700"
                  }`}
                >
                  {savingCustom ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ New Payout Request Modal */}
        {newPayoutModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 w-[90%] max-w-md">
              <h2 className="text-lg font-semibold mb-3">
                Create Payout Request
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Search for an event and request payout.
              </p>

              <div className="mb-4">
                <input
                  type="text"
                  value={searchEvent}
                  onChange={(e) => {
                    setSearchEvent(e.target.value);
                    fetchEventList(e.target.value);
                  }}
                  placeholder="Search event by name or ID"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200"
                />

                {/* Event results dropdown */}
                {eventResults.length > 0 && (
                  <ul className="border border-gray-200 rounded-lg mt-2 max-h-40 overflow-y-auto">
                    {eventResults.map((evt: any) => (
                      <li
                        key={evt.eventId}
                        onClick={() => {
                          setSelectedEvent(evt);
                          setSearchEvent(evt.eventName);
                          setEventResults([]);
                        }}
                        className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                          selectedEvent?.eventId === evt.eventId
                            ? "bg-blue-100"
                            : ""
                        }`}
                      >
                        <span className="font-medium">{evt.eventName}</span>{" "}
                        <span className="text-gray-500 text-sm">
                          (ID: {evt.eventId})
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex justify-end gap-3">
                {!creating && (
                  <button
                    onClick={() => setNewPayoutModal(false)}
                    className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                )}
                <button
                  disabled={creating}
                  onClick={handleCreatePayout}
                  className={`px-4 py-2 text-sm text-white rounded ${
                    creating
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {creating ? "Processing..." : "Create Request"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
